/**
 * "Select your iPhone color" — overlays a recolored camera-plateau patch on
 * every scene-1 render in scope (PDP main slide + thumbnail, or every
 * collection card) for iPhone 18 Pro / 18 Pro Max. Never touches the variant.
 * See snippets/phone-color-picker.liquid for the config JSON.
 *
 * Geometry: the patch box is stored as fractions of the 2000x2000 render. It
 * is positioned from the image's ACTUAL drawn rectangle (object-fit aware),
 * not from its container — the PDP letterboxes the square render on mobile
 * and cards crop it, and a container-relative patch drifts in both cases
 * (mobile QA 2026-09-11).
 *
 * Safety: an overlay is only drawn on an image whose filename matches the
 * renderer's scene-1 naming for a device we have patches for. Listings whose
 * gallery doesn't match keep the picker hidden, so nothing is ever drawn in
 * the wrong place.
 */
(() => {
  const STORAGE_KEY = 'blunt:phone-color';
  const COLORS = ['silver', 'black', 'glacier', 'burgundy'];
  const NAMES = { silver: 'Silver', black: 'Black', glacier: 'Glacier', burgundy: 'Burgundy' };
  const OVERLAY_CLASS = 'phone-color-overlay';
  const IMG_SELECTOR = '.product__media img, .thumbnail img, .card__media img';

  function readStored() {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return COLORS.includes(v) ? v : null;
    } catch (e) {
      return null;
    }
  }
  function store(color) {
    try {
      localStorage.setItem(STORAGE_KEY, color);
    } catch (e) {
      /* private mode etc. — selection still works for this page */
    }
  }

  let current = null;
  function currentColor() {
    if (current) return current;
    const fromUrl = (new URLSearchParams(window.location.search).get('phone') || '').toLowerCase();
    if (COLORS.includes(fromUrl)) {
      store(fromUrl);
      current = fromUrl;
    } else {
      current = readStored() || 'silver';
    }
    return current;
  }

  function parseConfig(picker) {
    const el = picker.querySelector('script[data-phone-color-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // ── Geometry ──────────────────────────────────────────────────────────────
  // The element the overlay is positioned against. Cards: the .card__media
  // wrapper (NOT inside .media — Dawn's hover rules key off img:only-child
  // there). PDP: the .media box. Thumbnails: the button.
  function hostFor(img) {
    const host = img.closest('.card__media') || img.closest('.thumbnail') || img.parentElement;
    if (!host) return null;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    return host;
  }

  // Rectangle of the drawn bitmap inside the <img> box, honouring object-fit
  // (object-position is center everywhere in this theme). Before the image
  // has loaded naturalWidth is 0; our renders are square, so assume 1:1.
  function drawnRect(img) {
    const r = img.getBoundingClientRect();
    const nw = img.naturalWidth || 1;
    const nh = img.naturalHeight || 1;
    const fit = getComputedStyle(img).objectFit || 'fill';
    let w = r.width;
    let h = r.height;
    if (fit === 'contain' || fit === 'cover' || fit === 'scale-down') {
      const s = fit === 'cover' ? Math.max(r.width / nw, r.height / nh) : Math.min(r.width / nw, r.height / nh);
      w = nw * s;
      h = nh * s;
    }
    return { box: r, left: r.left + (r.width - w) / 2, top: r.top + (r.height - h) / 2, width: w, height: h };
  }

  const overlays = new Set();
  function place(overlay) {
    const { img, host, box } = overlay._pc;
    if (!img.isConnected || !host.isConnected) {
      overlay.remove();
      overlays.delete(overlay);
      return;
    }
    const hr = host.getBoundingClientRect();
    const d = drawnRect(img);
    const left = d.left - hr.left + box.left * d.width;
    const top = d.top - hr.top + box.top * d.height;
    overlay.style.left = left + 'px';
    overlay.style.top = top + 'px';
    overlay.style.width = box.width * d.width + 'px';
    overlay.style.height = box.height * d.height + 'px';
    // Card hover zooms the image about ITS center; scale the overlay about
    // that same point so it stays glued to the plateau.
    overlay.style.transformOrigin = `${d.box.left + d.box.width / 2 - hr.left - left}px ${d.box.top + d.box.height / 2 - hr.top - top}px`;
  }
  function placeAll() {
    overlays.forEach(place);
  }

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => placeAll()) : null;

  function removeOverlays() {
    overlays.forEach((o) => o.remove());
    overlays.clear();
    if (resizeObserver) resizeObserver.disconnect();
  }

  function addOverlay(img, url, box) {
    const host = hostFor(img);
    if (!host) return;
    const overlay = document.createElement('span');
    overlay.className = OVERLAY_CLASS;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText =
      // z-index 4: on mobile the card's .card__media-link is absolute with
      // z-index 3 and painted over a z-index 1 overlay (mobile QA 2026-09-11).
      'position:absolute;display:block;pointer-events:none;background-repeat:no-repeat;background-size:100% 100%;z-index:4;' +
      `background-image:url("${url}");`;
    overlay._pc = { img, host, box };
    host.appendChild(overlay);
    overlays.add(overlay);
    place(overlay);
    if (resizeObserver) {
      resizeObserver.observe(img);
      resizeObserver.observe(host);
    }
    // Lazy card images: don't show a floating plateau on a blank card —
    // keep the overlay invisible until the render underneath has painted.
    if (!img.complete || !img.naturalWidth) {
      overlay.style.visibility = 'hidden';
      img.addEventListener(
        'load',
        () => {
          place(overlay);
          overlay.style.visibility = '';
        },
        { once: true }
      );
    }
  }

  // ── Apply ─────────────────────────────────────────────────────────────────
  function matchesIn(scope, devices) {
    const found = [];
    const imgs = scope.querySelectorAll(IMG_SELECTOR);
    for (const [, d] of Object.entries(devices)) {
      const re = new RegExp(d.pattern, 'i');
      const reAlt = d.altPattern ? new RegExp(d.altPattern, 'i') : null;
      imgs.forEach((img) => {
        const src = img.getAttribute('src') || img.currentSrc || '';
        const alt = (img.getAttribute('alt') || '').trim();
        // Filename (D2L listings) or alt text (older listings with random
        // filenames whose alt encodes device + angle) — same scene geometry.
        if (re.test(src) || (reAlt && reAlt.test(alt))) found.push({ img, d });
      });
    }
    return found;
  }

  function apply(picker, color) {
    const cfg = parseConfig(picker);
    if (!cfg || !cfg.devices) return;
    const scope = cfg.scope === 'page' ? document : picker.closest('.shopify-section') || document;
    const found = matchesIn(scope, cfg.devices);
    if (!found.length) {
      picker.hidden = true; // nothing here we can recolor
      return;
    }
    picker.hidden = false;

    picker.querySelectorAll('[data-phone-color]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.phoneColor === color));
    });
    const label = picker.querySelector('[data-phone-color-selected]');
    if (label) label.textContent = NAMES[color] || color;

    if (color === 'silver') return; // the live render already IS silver
    const seen = new Set();
    found.forEach(({ img, d }) => {
      if (seen.has(img)) return;
      seen.add(img);
      const url = d.patches && d.patches[color];
      if (url) addOverlay(img, url, d.box);
    });
  }

  function applyAll() {
    removeOverlays();
    const pickers = document.querySelectorAll('[data-phone-color-picker]');
    pickers.forEach((p) => apply(p, currentColor()));
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-phone-color]');
    if (!btn) return;
    const color = btn.dataset.phoneColor;
    if (!COLORS.includes(color)) return;
    event.preventDefault();
    current = color;
    store(color);
    applyAll();
    const picker = btn.closest('[data-phone-color-picker]');
    const cfg = picker ? parseConfig(picker) : null;
    const surface = cfg && cfg.scope === 'page' ? 'collection' : 'pdp';
    const devices = cfg && cfg.devices ? Object.keys(cfg.devices).join(',') : '';
    const detail = { color, surface, devices, path: window.location.pathname };
    document.dispatchEvent(new CustomEvent('phone-color:change', { bubbles: true, detail }));
    // Analytics — three sinks so it shows up wherever the team looks:
    //  GTM dataLayer (custom event `phone_color_select`), GA4 directly via
    //  gtag (Reports → Events / Realtime), and Shopify customer events for
    //  any web pixel (`phone_color_select`).
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'phone_color_select', phone_color: color, phone_color_surface: surface, phone_color_devices: devices });
    }
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'phone_color_select', { phone_color: color, surface, devices, page_path: detail.path });
      } catch (e) {
        /* analytics must never break the picker */
      }
    }
    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
      try {
        Shopify.analytics.publish('phone_color_select', detail);
      } catch (e) {
        /* ditto */
      }
    }
  });

  // Re-apply whenever the page re-renders around us: product-info.js swaps the
  // gallery and <variant-selects> (the latter through a view transition that
  // lands AFTER variantChange), facets and infinite scroll replace the
  // collection grid. Timers, never requestAnimationFrame — rAF is paused in
  // background tabs and while a view transition is capturing.
  let scheduled = false;
  function scheduleApply() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      applyAll();
      setTimeout(applyAll, 600);
    }, 0);
  }
  const RELEVANT =
    '[data-phone-color-picker], media-gallery, .product__media, .thumbnail, variant-selects, .card__media, #product-grid, #ProductGridContainer';
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1 || node.classList.contains(OVERLAY_CLASS)) continue;
        if (node.matches(RELEVANT) || node.querySelector(RELEVANT)) {
          scheduleApply();
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => scheduleApply());
  }
  document.addEventListener('product-info:loaded', () => scheduleApply());
  window.addEventListener('resize', placeAll);
  window.addEventListener('orientationchange', () => setTimeout(placeAll, 300));
  window.addEventListener('load', placeAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAll);
  } else {
    applyAll();
  }
})();
